import {z} from 'zod';

import {defaultTo, equals, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish, precisionCalculation} from 'shared/web';

type EditItemQuantitySchemaProps = {
  dispensingUnit: number;
  currentQuantity: number | Nullish;
  minQuantity: number | Nullish;
  maxQuantity: number | Nullish;
};

/**
 * Quantity rule of an Edit Item form, with the same rules and messages as the basket.
 * The saved quantity is always accepted, so an item that no longer meets the rules
 * (e.g. after a partial return) can be saved unchanged.
 */
export const getEditItemQuantitySchema = (props: EditItemQuantitySchemaProps) => {
  let limitsSchema = z.number().min(defaultTo(MIN_QUANTITY_WITHOUT_LIMIT, props.minQuantity));

  if (isNotNil(props.maxQuantity)) {
    limitsSchema = limitsSchema.max(props.maxQuantity);
  }

  const rulesSchema = limitsSchema.refine(
    (value) => precisionCalculation.modulo(value, props.dispensingUnit) === 0,
    `${i18n.t('entity.warehouse.labels.quantityMustBeMultipleOfDispensingUnit')} (${props.dispensingUnit})`
  );

  return z.number().superRefine((value, context) => {
    if (equals(value, props.currentQuantity)) {
      return;
    }

    rulesSchema.safeParse(value).error?.issues.forEach((issue) => context.addIssue(issue));
  });
};

const MIN_QUANTITY_WITHOUT_LIMIT = 0.0001;
